import FileActions from "components/FileDownloadButtons";
import React from "react";

interface EvidenceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  evidence: any;
}

const EvidenceDetailModal: React.FC<EvidenceDetailModalProps> = ({
  isOpen,
  onClose,
  evidence,
}) => {
  if (!isOpen || !evidence) return null;

  const evidenceDetails = [
    { label: "Collected By", value: evidence.Collected_by, key: "collected_by" },
    { label: "Collection Date", value: evidence.Collected_date, key: "collected_date" },
    { label: "Evidence Type", value: evidence.evidence_type, key: "evidence_type" },
    { label: "Chain of Custody", value: evidence.chain_of_custody, key: "custody" }
  ].filter(item => item.value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4 overflow-hidden border border-gray-200">
        {/* Header with dark red theme */}
        <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Evidence Record</h2>
              <p className="text-sm text-red-100 mt-1 opacity-90">Forensic Evidence Documentation</p>
            </div>
            <button
              onClick={onClose}
              className="text-red-200 hover:text-white text-2xl font-light transition-colors hover:bg-red-700 w-8 h-8 flex items-center justify-center rounded-full"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div className="space-y-8">
            {/* Evidence Information Section */}
            <section>
              <div className="flex items-center mb-4">
                <div className="h-6 w-1 bg-red-700 rounded-r mr-3"></div>
                <h3 className="text-base font-semibold text-gray-800">Evidence Details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4">
                {evidenceDetails.map((item) => (
                  <div key={item.key} className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-sm font-normal text-gray-800 bg-gray-50 px-3 py-2 rounded border border-gray-200 min-h-[40px] flex items-center">
                      {item.value}
                    </p>
                  </div>
                ))}
                
                {/* Remarks Field - Full Width */}
                {evidence.Remark && (
                  <div className="md:col-span-2 space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Investigation Remarks
                    </p>
                    <p className="text-sm font-normal text-gray-800 bg-gray-50 px-3 py-3 rounded border border-gray-200 min-h-[60px] whitespace-pre-wrap">
                      {evidence.Remark}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Evidence Files Section */}
            {evidence.attachment_details?.length > 0 && (
              <section>
                <div className="flex items-center mb-4">
                  <div className="h-6 w-1 bg-red-800 rounded-r mr-3"></div>
                  <h3 className="text-base font-semibold text-gray-800">
                    Evidence Files <span className="text-sm font-normal text-red-600 ml-2">({evidence.attachment_details.length})</span>
                  </h3>
                </div>
                <div className="pl-4">
                  <div className="space-y-3">
                    {evidence.attachment_details.map((file: any, index: number) => (
                      <div
                        key={file?.document_dms_uuid || index}
                        className="bg-red-50 rounded-lg border border-red-100 p-4 hover:bg-red-100/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">
                                    {file?.file_details?.file_name || `Evidence Document ${index + 1}`}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                      EVID-{index + 1}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                      PDF Document
                                    </span>
                                    {file?.file_details?.file_size && (
                                      <span className="text-xs text-gray-500">
                                        {file.file_details.file_size}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {file?.file_details?.upload_date && (
                                <p className="text-xs text-gray-500 mt-2">
                                  <span className="font-medium">Uploaded:</span> {file.file_details.upload_date}
                                </p>
                              )}
                            </div>
                          </div>
                          <FileActions fileUrl={file?.file_details?.url || ""} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Metadata Section */}
            <section className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {evidence.created_at && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Record Created
                    </p>
                    <p className="text-sm font-normal text-gray-600">
                      {evidence.created_at}
                    </p>
                  </div>
                )}
              </div>
              {evidence.evidence_status && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Evidence Status
                  </p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    evidence.evidence_status === 'Active' 
                      ? 'bg-green-100 text-green-800' 
                      : evidence.evidence_status === 'Archived'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {evidence.evidence_status}
                  </span>
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-red-50 via-white to-red-50 px-6 py-3 border-t border-red-100">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 font-medium">
              Evidence Record • Chain of Custody Document
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm hover:shadow"
            >
              Close Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvidenceDetailModal;